import _ from 'lodash'
import React, { ChangeEvent, useState } from 'react'
import {
  Box,
  Flex,
  Heading,
  ButtonPrimary,
  FullBlockSpinner
} from 'front-end/src/components'
import { css } from '@emotion/react'
import { Dropdown } from 'front-end/src/components/common/dropdown/Dropdown'
import IconButton from 'front-end/src/components/common/form/button/IconButton'
import { Checkbox } from 'front-end/src/components/common/form/checkbox/Checkbox'
import IconOptionsVertical from 'front-end/src/components/common/icon/IconOptionsVertical'
import TableExtendedOld from 'front-end/src/components/common/table-extended-old/TableExtendedOld'
import Tooltip from 'front-end/src/components/common/tooltip/Tooltip'
import UserAvatar from 'front-end/src/components/common/user/user-avatar/UserAvatar'
import {
  InviteesIndirectOrderColumnEnum,
  SqlOrderDirectionEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { formatDateAndTimeShort } from 'front-end/src/utils/dateHelpers'
import { AccessBanner } from 'front-end/src/components/common/access-banner/AccessBanner'
import Message, {
  MessageTypes
} from '../../../../../components/common/message/Message'
import { IInviteesFilter } from '../../types/IInvitee'
import InvitedGroupsList from '../InvitedGroupsList'
import { EventInviteModal } from './event-invite-modal/EventInviteModal'
import { InviteeActionsMenuList } from './InviteeActionsMenuList'
import { InviteeListHeader } from './InviteeListHeader'
import {
  InviteeItemFragment,
  useEventInviteesQuery,
  useRecurringEventInviteesQuery
} from './AttendanceTab.codegen'
import { brandColors } from 'shared/basicPalette'
import { fontWeights } from 'front-end/src/components/chakraThemeExtension'
import { Plural, Trans, t } from '@lingui/macro'
import dayjs from 'shared/dayjs'
import {
  getStatus,
  StatusEventStatusType,
  StatusOfEventProps,
  StatusRSVPAccepted,
  StatusRSVPDeclined,
  StatusRSVPNoResponse,
  StatusUserAttended,
  StatusUserDidNotAttend
} from 'front-end/src/pages/manage-events/components/event-status/EventStatus'
import './AttendanceTab.less'
import { useFormikContext } from 'formik'
import {
  ManageEventDetailDayjs,
  useEventForEditingQuery
} from '../../EventEditPage'
import { NbSp } from 'front-end/src/utils/NbSp'
import { useManageEventContext } from '../../../ManageEventContext'
import { ExportInviteesModal } from './ExportInviteesModal'
import { useDisclosure } from '@chakra-ui/react'
import { IconPlus } from 'front-end/src/components/common/icon/IconPlus'
import { ExportEventAttendanceCsvButton } from './ExportEventAttendanceCsvButton'
import { ExportRecurringEventAttendanceModalCsvButton } from './ExportRecurringEventAttendanceModalCsvButton'

interface AttendanceTabState {
  allSelected: boolean
  filters: IInviteesFilter[]
  page: number
  pageSize: number
  showInviteModal: boolean
  selectedInvitees: InviteeItemFragment[]
  search: string
  orderColumn: InviteesIndirectOrderColumnEnum
  orderDirection: SqlOrderDirectionEnum
}

const AttendanceTab = () => {
  const disclosure = useDisclosure()
  const { event, isRecurringSeries, isRecurringInstance } =
    useManageEventContext()
  const { refetch: refetchEvent } = useEventForEditingQuery(event.id)
  const { setFieldValue, values } = useFormikContext<ManageEventDetailDayjs>()

  const [state, setState] = useState<AttendanceTabState>({
    allSelected: false,
    filters: [],
    showInviteModal: false,
    selectedInvitees: [] as any[],
    pageSize: 20,
    page: 1,
    orderColumn: InviteesIndirectOrderColumnEnum.display_name,
    orderDirection: SqlOrderDirectionEnum.ASC,
    search: ''
  })

  const {
    data: eventInviteesData,
    loading: eventInviteesLoading,
    refetch: eventInviteesRefetch
  } = useEventInviteesQuery({
    fetchPolicy: 'network-only',
    variables: {
      eventId: event.id,
      filters: [], // setting state.filters causes issues, solved with refetch and variables
      orderBy: state.orderColumn,
      orderDirection: state.orderDirection,
      offset: (state.page - 1) * state.pageSize,
      limit: state.pageSize
    },
    skip: isRecurringSeries
  })

  const {
    data: recEventInviteesData,
    loading: recEventInviteesLoading,
    refetch: recEventInviteesRefetch
  } = useRecurringEventInviteesQuery({
    fetchPolicy: 'network-only',
    variables: {
      eventId: event.id,
      filters: [], // setting state.filters causes issues, solved with refetch and variables
      orderBy: state.orderColumn,
      orderDirection: state.orderDirection,
      offset: (state.page - 1) * state.pageSize,
      limit: state.pageSize
    },
    skip: !isRecurringSeries
  })

  const loading = eventInviteesLoading || recEventInviteesLoading
  const refetch = isRecurringSeries
    ? recEventInviteesRefetch
    : eventInviteesRefetch

  const inviteesIncludingIndirect =
    eventInviteesData?.admin?.event?.eventInvitees ||
    recEventInviteesData?.admin?.recurringEvent?.recurringEventInvitees

  const totalCount =
    eventInviteesData?.admin?.event?.eventInviteesTotalCount ||
    recEventInviteesData?.admin?.recurringEvent
      ?.recurringEventInviteesTotalCount ||
    0

  if (loading && !totalCount) {
    return <FullBlockSpinner />
  }

  const getRSVP = (data) => {
    if (data.attending === null) {
      return <StatusRSVPNoResponse />
    }
    if (data.attending === true) {
      return <StatusRSVPAccepted />
    }
    if (data.attending === false) {
      return <StatusRSVPDeclined />
    }
  }

  const getAttendance = (data) => {
    if (data.present === true) {
      return <StatusUserAttended data-test="AttendedStatus" />
    }
    if (data.present === false) {
      return <StatusUserDidNotAttend data-test="DidNotAttendStatus" />
    } else return false
  }

  const isAttendanceAllow =
    getStatus(event as StatusOfEventProps) ===
      StatusEventStatusType.INPROGRESS ||
    getStatus(event as StatusOfEventProps) === StatusEventStatusType.COMPLETED

  const changeState = (changes: Partial<AttendanceTabState>) => {
    setState({
      ...state,
      ...changes
    })
  }

  const onSelectionChange = (
    invitee: InviteeItemFragment,
    selected: boolean
  ) => {
    if (selected) {
      changeState({
        selectedInvitees: [...state.selectedInvitees, invitee]
      })
    } else {
      changeState({
        selectedInvitees: _.filter(
          state.selectedInvitees,
          function (userEvent) {
            return userEvent.user_id !== invitee.user_id
          }
        )
      })
    }
  }

  const tableColumns = [
    {
      Header: (
        <Box mr="8px">
          <Checkbox
            aria-label={t`Selected`}
            isChecked={state.allSelected}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              changeState({
                selectedInvitees: event.currentTarget.checked ? invitees : [],
                allSelected: event.currentTarget.checked
              })
            }}
          />
        </Box>
      ),
      accessor: 'checked',
      id: 'checked',
      width: 40,
      sortable: false,
      Cell: (row) => (
        <Checkbox
          aria-label={t`Selected`}
          isChecked={
            !!state.selectedInvitees.find(({ user_id }) => {
              return user_id === row.original.user_id
            })
          }
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            onSelectionChange(row.original, event.currentTarget.checked)
          }}
        />
      )
    },
    {
      Header: t`Name/email`,
      accessor: 'display_name',
      id: InviteesIndirectOrderColumnEnum.display_name,
      Cell: (row) => (
        <Flex className="overflowEllipsis">
          <UserAvatar
            user={{
              display_name: row.original.display_name,
              image_url: row.original.image_url
            }}
          />
          <Flex flexDirection="column" className="overflowEllipsis">
            <Box
              className="overflowEllipsis"
              fontSize="15px"
              color={brandColors.grey.dark}
              fontWeight={fontWeights.bold}
              lineHeight="20px"
            >
              {row.original.display_name}
            </Box>
            <Box
              className="overflowEllipsis"
              fontSize="12px"
              color={brandColors.grey.mid}
            >
              {row.original.email}
            </Box>
          </Flex>
        </Flex>
      )
    },
    {
      Header: t`Via`,
      sortable: false,
      Cell: (row) => (
        <Box
          className="overflowEllipsis"
          fontSize="13px"
          color={brandColors.grey.mid}
        >
          {row.original.certification_id ? (
            <>
              <Trans>Via certification</Trans>{' '}
              <Box
                className="overflowEllipsis"
                color={brandColors.grey.dark}
                as="span"
              >
                {row.original.certification_name}
              </Box>
            </>
          ) : (
            <>
              {row.original.group_id && (
                <>
                  <Trans>Via group</Trans>{' '}
                  <Box
                    className="overflowEllipsis"
                    color={brandColors.grey.dark}
                    as="span"
                  >
                    {row.original.group_name}
                  </Box>
                </>
              )}
            </>
          )}
          {event.facilitator?.id && row.original.id === event.facilitator?.id && (
            <>
              <Trans>Facilitator</Trans>
            </>
          )}
        </Box>
      )
    },
    {
      Header: t`Invited`,
      accessor: 'created_at',
      id: InviteesIndirectOrderColumnEnum.created_at,
      Cell: (row) => (
        <Box
          className="overflowEllipsis"
          fontSize="13px"
          color={brandColors.grey.mid}
        >
          <Tooltip text={formatDateAndTimeShort(row.value)}>
            {dayjs(row.value).fromNow()}
          </Tooltip>
        </Box>
      )
    },
    {
      Header: t`RSVP`,
      accessor: 'attending',
      show: !isRecurringSeries,
      id: InviteesIndirectOrderColumnEnum.attending,
      Cell: (row) => {
        return getRSVP(row.original)
      }
    },
    {
      Header: t`Attendance`,
      accessor: 'present',
      id: InviteesIndirectOrderColumnEnum.present,
      show: isAttendanceAllow && !isRecurringSeries,
      Cell: (row) => {
        return getAttendance(row.original)
      }
    },
    {
      Header: '',
      id: 'options',
      width: 40,
      sortable: false,
      Cell: (row) => (
        <Dropdown
          key={0}
          content={
            <InviteeActionsMenuList
              isRecurringSeries={isRecurringSeries}
              isRecurringInstance={isRecurringInstance}
              selectProps={{
                eventId,
                refetchInvitees: refetch,
                selectedInvitees: [row.original],
                groupId: row.original.group_id
              }}
              allowRemove={
                !row.original.certification_id && !row.original.group_id
              }
              allowAttendance={isAttendanceAllow}
              reasonNotAllowRemove={
                row.original.certification_id
                  ? t`Certification`
                  : row.original.group_id
                  ? t`Group`
                  : null
              }
            />
          }
        >
          <IconButton icon={<IconOptionsVertical />} hint={t`Options`} />
        </Dropdown>
      )
    }
  ]

  const {
    showInviteModal,
    page,
    selectedInvitees,
    orderColumn,
    orderDirection
  } = state

  const { id: eventId, end_time, published_date } = event
  const invitees = inviteesIncludingIndirect || []

  return (
    <>
      {!published_date && (
        <Message type={MessageTypes.WARNING} mb="16px">
          <Trans>
            Please publish this event to be able to send invitations.
          </Trans>
        </Message>
      )}
      {!!end_time && dayjs(end_time).isBefore(dayjs()) && (
        <Message type={MessageTypes.INFO} mb="16px">
          <Trans>This event has ended</Trans>
          <NbSp />
          {dayjs(end_time).fromNow()}
        </Message>
      )}
      <Box
        className="AttendanceTab"
        css={css`
          .TableExtended__filter {
            width: 100%;
            margin-right: 0;
            justify-content: space-between;
          }
          .TableExtendedHeader {
            padding-left: 0;
            padding-right: 0;
          }
          .TableExtended .ReactTable .rt-tr {
            padding-left: 0;
          }
          .TableExtended .ReactTable .rt-tbody .rt-td {
            padding-left: 5px;
          }
        `}
      >
        <Box pb="16px">
          <AccessBanner
            subject={t`Event`}
            isPublic={values.is_open}
            onToggle={(value) => setFieldValue('is_open', value === 'true')}
            privateDesc={t`Only invited members will be allowed to view or RSVP`}
            publicDesc={t`Anyone in your organisation will be allowed to view or RSVP`}
          />
        </Box>

        {(!isAttendanceAllow || isRecurringSeries) && (
          <>
            <Heading size="md" className="AttendanceTab__title" mb="4px">
              <Trans>Groups</Trans>
            </Heading>
            <Box
              className="AttendanceTab__hint"
              color={brandColors.grey[500]}
              mb="16px"
            >
              <Trans>
                All group members will have access to this event occurrence and
                will appear in the people list below.
              </Trans>
            </Box>
            <InvitedGroupsList
              isRecurringSeries={isRecurringSeries}
              eventId={eventId}
              onRemove={refetch}
            />
          </>
        )}
        <Heading size="md" className="AttendanceTab__title" mb="4px">
          <Trans>People</Trans>
        </Heading>
        <Box
          className="AttendanceTab__hint"
          color={brandColors.grey[500]}
          mb="16px"
        >
          <Trans>The following people have access to this event</Trans>
          <NbSp />
          {isRecurringSeries ? t`series` : null}
          {isRecurringInstance ? t`occurrence` : null}.
        </Box>

        <TableExtendedOld
          data={invitees}
          loading={loading}
          manual
          columns={tableColumns}
          defaultPageSize={state.pageSize}
          showPageSizeOptions={false}
          resizable={false}
          minRows={1}
          pages={Math.ceil(totalCount / state.pageSize)}
          page={(page ?? 0) - 1}
          defaultSortDesc
          onPageChange={(newPage) => {
            changeState({ page: newPage + 1, allSelected: false })
            refetch()
          }}
          defaultSorted={[
            {
              id: orderColumn,
              desc: orderDirection === SqlOrderDirectionEnum.DESC
            }
          ]}
          onSortedChange={(newSorted) => {
            const descendingOrder = newSorted[0] ? newSorted[0].desc : false
            changeState({
              orderColumn: newSorted[0]
                ? (newSorted[0].id as InviteesIndirectOrderColumnEnum)
                : InviteesIndirectOrderColumnEnum.display_name,
              orderDirection: descendingOrder
                ? SqlOrderDirectionEnum.DESC
                : SqlOrderDirectionEnum.ASC
            })
            refetch()
          }}
          onSearchChange={(searchVal) => {
            changeState({
              search: searchVal
            })
          }}
          paginationContentBefore={
            <Box color={brandColors.grey.mid}>
              <Plural
                value={totalCount}
                _0={null}
                one={`# user`}
                other={`# users`}
              />
            </Box>
          }
          filterFields={['display_name']}
          showSearchFilter={false}
          extendFilter={
            <InviteeListHeader
              onSelectAllChange={(selected: boolean) => {
                if (selected) {
                  changeState({
                    selectedInvitees: invitees
                  })
                } else {
                  changeState({
                    selectedInvitees: []
                  })
                }
              }}
              selectedInvitees={selectedInvitees}
              onFilterChange={(filters) => {
                changeState({ filters })
                refetch({ filters })
              }}
            >
              <Box>
                <EventInviteModal
                  show={showInviteModal}
                  invitees={invitees}
                  onInvite={async () => {
                    await refetch()
                    await refetchEvent()

                    changeState({ showInviteModal: false })
                  }}
                  onHide={() => {
                    changeState({ showInviteModal: false })
                  }}
                />
                <ExportEventAttendanceCsvButton
                  disclosure={disclosure}
                  filters={state.filters}
                  count={totalCount}
                />
                <ButtonPrimary
                  ml="8px"
                  disabled={!event.start_time || !event.published_date}
                  onClick={() => {
                    changeState({ showInviteModal: true })
                  }}
                  leftIcon={<IconPlus />}
                  dataTest="InvitePeopleButton"
                >
                  <Trans>Add people and groups</Trans>
                </ButtonPrimary>
              </Box>
            </InviteeListHeader>
          }
        />
      </Box>
      <ExportInviteesModal disclosure={disclosure}>
        <ExportRecurringEventAttendanceModalCsvButton
          disclosure={disclosure}
          filters={state.filters}
          count={totalCount}
        />
      </ExportInviteesModal>
    </>
  )
}

export default AttendanceTab
